import { useMemo, useState } from "react";
import { ArrowRight, LinkIcon, Link2Off, Palette, Shapes, Sparkles } from "lucide-react";
import { Sheet } from "./ui/Sheet";
import { Button } from "./ui/Button";
import { FurniturePreview } from "./FurniturePreview";
import { TemplateParamsEditor } from "./inspector/TemplateParamsEditor";
import { TEMPLATES } from "../lib/furniture/templates";
import { MATERIALS, materialInfo } from "../lib/furniture/materials";
import type { FurnitureLike } from "../lib/furniture/resolve";
import { cn } from "../lib/cn";
import {
  AiQuotaExceededError,
  importProductFromUrl,
  ProductImportFailedError,
  ProductImportInvalidUrlError,
  ProductImportPremiumRequiredError,
  ProductImportSignInRequiredError,
  ProductImportUnavailableError,
  validateProductUrl,
  type ExtractedProduct,
} from "../lib/productImport";
import { useProducts } from "../lib/products";
import {
  getProductModelJob,
  linkProductModelJob,
  startProductModelJob,
  useProductModelJob,
  type ProductModelJob,
} from "../lib/productModelJobs";
import { turnCapture } from "../lib/productCapture";
import { hasAccount, useAuth } from "../lib/auth";
import type { UpsellTrigger } from "../lib/events";
import { track } from "../lib/telemetry";
import { usePremium } from "../lib/premium";
import { quotaErrorMessageForPremiumClient } from "../lib/usage";
import type {
  FurnitureCategory,
  MaterialId,
  ParamValue,
  ProductCapture,
  TemplateId,
  WallMountCategory,
} from "../lib/types";
import { UsageBudgetWarning } from "./UsageMeter";
import { ProductModelStatus, type ProductModelState } from "./ProductModelStatus";

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "preview"; product: EditableProduct };

/** Mutable preview state. Mirrors {@link ExtractedProduct} but loosens
 *  the category so the user can flip kind without invalid combos. */
interface EditableProduct {
  name: string;
  kind: ExtractedProduct["kind"];
  width: number;
  depth: number;
  height: number;
  category: string;
  color: string;
  needsManualReview: boolean;
  notes?: string;
  metadata?: ExtractedProduct["metadata"];
  /** Parametric classification (furniture only). Editable in the preview. */
  template?: TemplateId;
  templateParams?: Record<string, ParamValue>;
  material?: MaterialId;
  palette?: string[];
  materials?: MaterialId[];
  dimensionSource?: ExtractedProduct["dimensionSource"];
  sourceUrl: string;
  sourceDomain: string;
  requestId?: string;
  /** The retailer's mesh, from the import itself. */
  capture?: ProductCapture;
  /** The user's "Turn model", applied to whichever mesh the piece gets. */
  modelYaw?: number;
}

/** The mesh the product will carry: the retailer's, else a finished
 *  rebuild from the photos, with the user's turn applied. */
function effectiveCapture(p: EditableProduct, job: ProductModelJob | null): ProductCapture | undefined {
  const capture = p.capture ?? (job?.status === "ready" ? job.capture : undefined);
  if (!capture) return undefined;
  return p.modelYaw === undefined ? capture : { ...capture, yaw: p.modelYaw };
}

function modelStateOf(p: EditableProduct, job: ProductModelJob | null): ProductModelState {
  if (p.capture) return p.capture.source;
  if (job?.status === "ready") return job.capture.source;
  if (job?.status === "building") return "building";
  if (job?.status === "failed") return "failed";
  return "none";
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Called when the user clicks "Save & place". The host (DesignView)
   * is responsible for placing the product on the canvas — this sheet
   * only handles fetch, preview, and save-to-catalog.
   */
  onPlace?: (productId: string) => void;
  /** Called when the backend rejects with `permission-denied` — caller
   *  is signed in but isn't Premium. The host closes the sheet and
   *  pops the upsell overlay. */
  onPremiumRequired?: (trigger: UpsellTrigger) => void;
  /** The session cannot import (signed out, anonymous, or the server
   *  rejected the call). The host closes this sheet and opens sign-in. */
  onSignInRequired?: () => void;
}

/** Sheet that hosts the "import product from URL" flow.
 *
 * Lifecycle:
 *   - idle     — URL input + submit.
 *   - loading  — fetching + extracting via the Cloud Function.
 *   - preview  — model returned a structured product; user can edit
 *                fields and either save-only or save-and-place.
 *   - error    — friendly message + retry.
 *
 * Visitors without an account never see this form. The host opens a
 * sign-in sheet instead, and a mid-request `ProductImportSignInRequiredError`
 * hands back through `onSignInRequired`.
 *
 * Gated client-side by the `product_url_import` feature flag (caller
 * checks this before rendering); this sheet doesn't re-check.
 */
export function ImportProductSheet({
  open,
  onClose,
  onPlace,
  onPremiumRequired,
  onSignInRequired,
}: Props) {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const addExtracted = useProducts((s) => s.addExtracted);
  const premium = usePremium();
  const auth = useAuth();

  // Live URL validation surfaces the inline error before submit so the
  // submit button can be disabled. We swallow throws here — the field
  // is just for previewing the validity, not gating yet.
  const urlValidationError = useMemo<string | null>(() => {
    if (url.trim().length === 0) return null;
    try {
      validateProductUrl(url);
      return null;
    } catch (e) {
      if (e instanceof ProductImportInvalidUrlError) return e.message;
      return null;
    }
  }, [url]);

  async function submit() {
    try {
      validateProductUrl(url);
    } catch (e) {
      if (e instanceof ProductImportInvalidUrlError) {
        setStatus({ kind: "error", message: e.message });
        return;
      }
      throw e;
    }
    setStatus({ kind: "loading" });
    try {
      const extracted = await importProductFromUrl(url);
      const galleryUrls = extracted.modelFallback?.galleryUrls;
      if (extracted.kind === "furniture" && !extracted.capture && galleryUrls?.length && extracted.requestId) {
        startProductModelJob({
          requestId: extracted.requestId,
          galleryUrls,
          width: extracted.width,
          depth: extracted.depth,
          height: extracted.height,
        });
      }
      setStatus({
        kind: "preview",
        product: {
          name: extracted.name,
          kind: extracted.kind,
          width: extracted.width,
          depth: extracted.depth,
          height: extracted.height,
          category: extracted.category,
          color: extracted.color,
          needsManualReview: extracted.needsManualReview,
          notes: extracted.notes,
          metadata: extracted.metadata,
          template: extracted.template,
          templateParams: extracted.templateParams,
          material: extracted.material,
          palette: extracted.palette,
          materials: extracted.materials,
          dimensionSource: extracted.dimensionSource,
          sourceUrl: extracted.sourceUrl,
          sourceDomain: extracted.sourceDomain,
          requestId: extracted.requestId,
          capture: extracted.capture,
        },
      });
    } catch (err) {
      if (err instanceof ProductImportSignInRequiredError) {
        setStatus({ kind: "idle" });
        onSignInRequired?.();
        onClose();
        return;
      }
      if (
        err instanceof ProductImportPremiumRequiredError ||
        err instanceof AiQuotaExceededError
      ) {
        // Free quota exhausted (post-migration) OR legacy premium gate.
        // If the client already thinks it's Premium (local override, or
        // a true subscriber hitting a misclassified error), keep the
        // sheet open — the upsell overlay auto-closes for isPremium
        // and would just flash. Otherwise bounce to the host upsell.
        if (premium.isPremium) {
          setStatus({
            kind: "error",
            message: quotaErrorMessageForPremiumClient(premium),
          });
          return;
        }
        setStatus({ kind: "idle" });
        onPremiumRequired?.(err instanceof AiQuotaExceededError ? "quota" : "premium_required");
        return;
      }
      const message =
        err instanceof ProductImportUnavailableError ||
        err instanceof ProductImportFailedError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Something went wrong importing that listing.";
      setStatus({ kind: "error", message });
    }
  }

  /**
   * Build the {@link ExtractedProduct} the store expects from the
   * possibly-user-edited preview state.
   */
  function buildExtracted(p: EditableProduct): ExtractedProduct {
    return {
      name: p.name,
      kind: p.kind,
      width: p.width,
      depth: p.depth,
      height: p.height,
      category: p.category as FurnitureCategory | WallMountCategory,
      color: p.color,
      needsManualReview: p.needsManualReview,
      notes: p.notes,
      metadata: p.metadata,
      // Shape only applies to floor furniture — mounts render flat.
      template: p.kind === "furniture" ? p.template : undefined,
      templateParams: p.kind === "furniture" ? p.templateParams : undefined,
      material: p.kind === "furniture" ? p.material : undefined,
      palette: p.palette,
      materials: p.materials,
      dimensionSource: p.dimensionSource,
      sourceUrl: p.sourceUrl,
      sourceDomain: p.sourceDomain,
      capture: p.kind === "furniture" ? effectiveCapture(p, getProductModelJob(p.requestId)) : undefined,
      requestId: p.requestId,
    };
  }

  function save(place: boolean) {
    if (status.kind !== "preview") return;
    const product = status.product;
    const id = addExtracted(buildExtracted(product));
    // A rebuild still running lands on the saved product (and the placed
    // piece) when it finishes.
    if (product.requestId && product.kind === "furniture") linkProductModelJob(product.requestId, id);
    if (place && onPlace) onPlace(id);
    reset();
    onClose();
  }

  function reset() {
    setUrl("");
    setStatus({ kind: "idle" });
  }

  function handleClose() {
    reset();
    onClose();
  }

  // No account: the host shows a sign-in sheet. Don't leave the URL form
  // and credit line underneath it.
  if (
    auth.status === "signed-out" ||
    (auth.status === "signed-in" && !hasAccount(auth.user))
  ) {
    return null;
  }

  return (
    <Sheet
      open={open}
      onClose={handleClose}
      title="Import from URL"
      side="right"
      width="min(440px, 96vw)"
    >
      <div className="pt-1" data-testid="import-product-sheet">
        {status.kind !== "preview" && (
          <>
            <p className="text-sm text-ink-500">
              Paste a product listing and we&apos;ll pull the dimensions and
              dominant color so you can drop the piece straight onto your
              room.
            </p>

            <div className="mt-5">
              <label
                htmlFor="import-product-url"
                className="text-xs font-medium text-ink-500 mb-2 block"
              >
                Listing URL
              </label>
              <div className="relative">
                <LinkIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-400 pointer-events-none"
                  aria-hidden
                />
                <input
                  id="import-product-url"
                  data-testid="import-product-url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && status.kind !== "loading") {
                      e.preventDefault();
                      void submit();
                    }
                  }}
                  placeholder="https://www.ikea.com/us/en/p/…"
                  className="w-full h-11 pl-9 pr-3 rounded-xl bg-white ring-1 ring-ink-200 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-ink-900/30 disabled:opacity-60"
                  disabled={status.kind === "loading"}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              {urlValidationError && (
                <p
                  className="mt-1.5 text-caption text-rose-700 flex items-center gap-1"
                  data-testid="import-product-url-error"
                >
                  <Link2Off className="size-3.5" />
                  {urlValidationError}
                </p>
              )}
              <p className="mt-1.5 text-caption text-ink-400">
                Works best with mainstream brand and retailer listings
                (IKEA, Wayfair, Crate &amp; Barrel, West Elm, etc.).
              </p>
            </div>

            {status.kind === "error" && (
              <div
                className="mt-4 p-3 rounded-xl bg-rose-50 ring-1 ring-rose-200 text-sm text-rose-800"
                data-testid="import-product-error"
              >
                {status.message}
              </div>
            )}

            <UsageBudgetWarning />

            <div className="mt-6 flex flex-col gap-2">
              <Button
                variant="primary"
                size="lg"
                onClick={submit}
                disabled={
                  status.kind === "loading" ||
                  url.trim().length === 0 ||
                  urlValidationError !== null
                }
                data-testid="import-product-submit"
              >
                {status.kind === "loading" ? (
                  <>
                    <Spinner />
                    Reading the listing…
                  </>
                ) : (
                  <>
                    <Sparkles className="size-4" />
                    Import product
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {status.kind === "preview" && (
          <ProductPreview
            product={status.product}
            onChange={(next) =>
              setStatus({ kind: "preview", product: next })
            }
            onSaveOnly={() => save(false)}
            onSavePlace={() => save(true)}
            onCancel={reset}
            canPlace={!!onPlace}
          />
        )}
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Preview card
// ---------------------------------------------------------------------------

const FURNITURE_CATEGORY_OPTIONS: { id: FurnitureCategory; label: string }[] = [
  { id: "seating", label: "Seating" },
  { id: "tables", label: "Tables" },
  { id: "beds", label: "Beds" },
  { id: "storage", label: "Storage" },
  { id: "appliance", label: "Appliances" },
  { id: "countertop", label: "Countertops" },
  { id: "decor", label: "Decor" },
  { id: "custom", label: "Other" },
];

const MOUNT_CATEGORY_OPTIONS: { id: WallMountCategory; label: string }[] = [
  { id: "tv", label: "TV" },
  { id: "art", label: "Art" },
  { id: "shelf", label: "Shelf" },
  { id: "mirror", label: "Mirror" },
  { id: "lighting", label: "Lighting" },
  { id: "custom", label: "Other" },
];

/** Picker category each template naturally belongs to — the category
 *  select follows the template so a "sectional" import lands in Seating. */
const TEMPLATE_CATEGORY: Record<TemplateId, FurnitureCategory> = {
  box: "custom",
  sofa: "seating",
  sectional: "seating",
  armchair: "seating",
  chair: "seating",
  stool: "seating",
  bench: "seating",
  ottoman: "seating",
  table: "tables",
  desk: "tables",
  bed: "beds",
  nightstand: "beds",
  dresser: "storage",
  wardrobe: "storage",
  bookshelf: "storage",
  "tv-stand": "storage",
  fridge: "appliance",
  washer: "appliance",
  stove: "appliance",
  counter: "countertop",
  island: "countertop",
  rug: "decor",
  lamp: "decor",
  plant: "decor",
};

/** The piece exactly as `placeImportedProduct` will build it, so the
 *  preview never lies about what lands on the canvas. */
function toPreviewFurniture(p: EditableProduct): FurnitureLike {
  const template = p.template ?? "box";
  return {
    name: p.name || "Imported product",
    width: Math.max(1, p.width),
    depth: Math.max(1, p.depth),
    height: Math.max(1, p.height),
    color: p.color,
    model: { kind: "parametric", template, params: { ...(p.templateParams ?? {}) } },
    material: { id: p.material ?? TEMPLATES[template].defaultMaterial, tint: p.color },
    frontKind: TEMPLATES[template].frontKind,
  };
}

function ProductPreview({
  product,
  onChange,
  onSaveOnly,
  onSavePlace,
  onCancel,
  canPlace,
}: {
  product: EditableProduct;
  onChange: (next: EditableProduct) => void;
  onSaveOnly: () => void;
  onSavePlace: () => void;
  onCancel: () => void;
  canPlace: boolean;
}) {
  const categoryOptions =
    product.kind === "furniture"
      ? FURNITURE_CATEGORY_OPTIONS
      : MOUNT_CATEGORY_OPTIONS;
  const isFurniture = product.kind === "furniture";
  const template: TemplateId = product.template ?? "box";
  const previewFurniture = useMemo(
    () => (isFurniture ? toPreviewFurniture(product) : null),
    [isFurniture, product]
  );
  const activeMaterial: MaterialId = product.material ?? TEMPLATES[template].defaultMaterial;
  const secondaryMaterials = (product.materials ?? []).filter((m) => m !== activeMaterial);
  const palette = product.palette && product.palette.length > 0 ? product.palette : [product.color];
  const modelJob = useProductModelJob(product.requestId);
  const capture = effectiveCapture(product, modelJob);

  function turnModel() {
    if (!capture) return;
    onChange({ ...product, modelYaw: turnCapture(capture).yaw });
    track("product_model_oriented", { source: capture.source });
  }

  function setTemplate(next: TemplateId) {
    if (next === template) return;
    const nextCategory = TEMPLATE_CATEGORY[next];
    onChange({
      ...product,
      template: next,
      templateParams: {},
      // Follow the template unless the user already picked a specific
      // category that still applies (e.g. "Other" stays if they chose it).
      category:
        nextCategory === "custom" || product.category === "custom" ? product.category : nextCategory,
    });
  }

  return (
    <div className="pt-1" data-testid="import-product-preview">
      {product.needsManualReview && (
        <div className="mb-4 p-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 text-sm text-amber-900">
          <div className="font-semibold">Double-check the dimensions</div>
          {product.notes && (
            <p className="mt-1 text-caption leading-snug">{product.notes}</p>
          )}
        </div>
      )}

      <div className="rounded-2xl bg-ink-900 text-white p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-caption uppercase font-semibold text-ink-300">
              <LinkIcon className="size-3.5" />
              From {product.sourceDomain || "listing"}
            </div>
            <input
              aria-label="Product name"
              value={product.name}
              onChange={(e) => onChange({ ...product, name: e.target.value })}
              className="mt-2 w-full bg-transparent text-base font-semibold focus:outline-none placeholder:text-ink-500"
              placeholder="Product name"
            />
            <a
              href={product.sourceUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-1 inline-block text-caption text-ink-400 hover:text-white underline underline-offset-2"
            >
              View listing
            </a>
            {isFurniture && (
              <div className="mt-2 text-caption text-ink-300">
                {TEMPLATES[template].label} · {materialInfo(activeMaterial).label}
              </div>
            )}
          </div>
          {previewFurniture && (
            <div
              className="shrink-0 rounded-xl bg-white p-1 shadow-inner"
              data-testid="import-product-preview-render"
            >
              <FurniturePreview
                furniture={previewFurniture}
                size={112}
                pad={10}
                title={`Preview of ${product.name || "imported product"}`}
              />
            </div>
          )}
        </div>
        {isFurniture && (
          <ProductModelStatus
            className="mt-3 border-t border-white/10 pt-3"
            tone="dark"
            state={modelStateOf(product, modelJob)}
            yaw={capture?.yaw}
            onTurn={turnModel}
          />
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <FieldStepper
          label="Kind"
          value={product.kind}
          options={[
            { id: "furniture", label: "Floor" },
            { id: "mount", label: "Wall mount" },
          ]}
          onChange={(k) => {
            // Reset category to the new kind's first option when we flip
            // between furniture/mount so we don't end up with a stale
            // category that doesn't apply.
            const next: EditableProduct = {
              ...product,
              kind: k as ExtractedProduct["kind"],
            };
            const validCats =
              k === "furniture"
                ? FURNITURE_CATEGORY_OPTIONS
                : MOUNT_CATEGORY_OPTIONS;
            if (!validCats.some((c) => c.id === product.category)) {
              next.category = validCats[0].id;
            }
            // Mounts don't have a meaningful depth; force to 0 so the
            // placement code doesn't try to make a 3D piece flat on the wall.
            if (k === "mount" && product.depth > 6) next.depth = 0;
            onChange(next);
          }}
        />
        <FieldStepper
          label="Category"
          value={product.category}
          options={categoryOptions.map((o) => ({ id: o.id, label: o.label }))}
          onChange={(c) => onChange({ ...product, category: c })}
        />
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <NumberField
          label="Width"
          value={product.width}
          onChange={(v) => onChange({ ...product, width: v })}
        />
        <NumberField
          label="Depth"
          value={product.depth}
          onChange={(v) => onChange({ ...product, depth: v })}
          allowZero={product.kind === "mount"}
        />
        <NumberField
          label="Height"
          value={product.height}
          onChange={(v) => onChange({ ...product, height: v })}
        />
      </div>
      <p className="mt-1 text-caption text-ink-400" data-testid="import-product-dimension-source">
        All values in inches.
        {product.dimensionSource === "listing" && " Read from the listing."}
        {product.dimensionSource === "estimated" && " Estimated — please verify."}
      </p>

      {isFurniture && (
        <div className="mt-4 rounded-2xl bg-ink-50 ring-1 ring-ink-100 p-3" data-testid="import-product-shape">
          <TemplateParamsEditor
            template={template}
            params={product.templateParams}
            onTemplateChange={setTemplate}
            onParamChange={(_id, _value, resolved) =>
              onChange({ ...product, templateParams: resolved })
            }
            selectTestId="import-product-template"
            history="off"
            header={
              <div className="flex items-center gap-1.5 text-label uppercase tracking-wide font-semibold text-ink-500">
                <Shapes className="size-3" />
                Shape
              </div>
            }
          />
        </div>
      )}

      {isFurniture && (
        <div className="mt-3 rounded-2xl bg-ink-50 ring-1 ring-ink-100 p-3" data-testid="import-product-material">
          <div className="mb-2 flex items-center gap-1.5 text-label uppercase tracking-wide font-semibold text-ink-500">
            <Palette className="size-3" />
            Material
            <span className="ml-auto text-caption normal-case tracking-normal font-medium text-ink-700">
              {materialInfo(activeMaterial).label}
              {secondaryMaterials.length > 0 && (
                <span className="text-ink-400">
                  {" "}
                  + {secondaryMaterials.map((m) => materialInfo(m).label).join(", ")}
                </span>
              )}
            </span>
          </div>
          <div className="grid grid-cols-8 gap-1.5" role="radiogroup" aria-label="Material">
            {MATERIALS.map((m) => {
              const active = m.id === activeMaterial;
              return (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={m.label}
                  title={m.label}
                  data-testid={`import-product-material-${m.id}`}
                  onClick={() => onChange({ ...product, material: m.id })}
                  className={cn(
                    "flex items-center justify-center rounded-lg p-0.5 ring-1 transition-all",
                    active
                      ? "bg-white ring-2 ring-accent-500 shadow-sm"
                      : "bg-white/60 ring-ink-100 hover:bg-white hover:ring-ink-300"
                  )}
                >
                  {previewFurniture && (
                    <FurniturePreview
                      furniture={{ ...previewFurniture, material: { id: m.id, tint: product.color } }}
                      size={38}
                      pad={3}
                      shadow={false}
                      lod={1}
                      title={m.label}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <div className="text-xs font-medium text-ink-500">Color</div>
        <label className="relative">
          <input
            type="color"
            value={product.color}
            onChange={(e) => onChange({ ...product, color: e.target.value })}
            className="absolute inset-0 opacity-0 cursor-pointer"
            aria-label="Product color"
          />
          <span
            className="block size-8 rounded-lg ring-1 ring-ink-200"
            style={{ background: product.color }}
            aria-hidden
          />
        </label>
        <span className="text-caption text-ink-500 tabular-nums">
          {product.color.toUpperCase()}
        </span>
        {palette.length > 1 && (
          <div className="ml-auto flex items-center gap-1.5" data-testid="import-product-palette">
            <span className="text-caption text-ink-400">Palette</span>
            {palette.map((c) => {
              const active = c.toLowerCase() === product.color.toLowerCase();
              return (
                <button
                  key={c}
                  type="button"
                  aria-label={`Use color ${c.toUpperCase()}`}
                  aria-pressed={active}
                  onClick={() => onChange({ ...product, color: c })}
                  className={cn(
                    "size-6 rounded-full ring-1 ring-black/10 transition-transform",
                    active ? "ring-2 ring-accent-500 scale-110" : "hover:scale-105"
                  )}
                  style={{ background: c }}
                />
              );
            })}
          </div>
        )}
      </div>

      {!product.needsManualReview && product.notes && (
        <p className="mt-2 text-caption text-ink-400" data-testid="import-product-notes">
          {product.notes}
        </p>
      )}

      {(product.metadata?.brand || product.metadata?.price) && (
        <div className="mt-4 flex items-center gap-3 text-caption text-ink-500">
          {product.metadata?.brand && (
            <span>
              <span className="text-ink-400">Brand: </span>
              {product.metadata.brand}
            </span>
          )}
          {product.metadata?.price && (
            <span>
              <span className="text-ink-400">Price: </span>
              {product.metadata.price}
            </span>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-2">
        {canPlace && (
          <Button
            variant="primary"
            size="lg"
            onClick={onSavePlace}
            data-testid="import-product-save-place"
          >
            <ArrowRight className="size-4" />
            Save &amp; place on canvas
          </Button>
        )}
        <Button
          variant="secondary"
          size="md"
          onClick={onSaveOnly}
          data-testid="import-product-save"
        >
          Save to my imports
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Start over
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small inputs (kept local — the codebase doesn't have a Select primitive)
// ---------------------------------------------------------------------------

function FieldStepper({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  onChange: (id: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-caption font-medium text-ink-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 px-3 rounded-xl bg-white ring-1 ring-ink-200 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-ink-900/30"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  allowZero = false,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  allowZero?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-caption font-medium text-ink-500">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => {
          const parsed = Number(e.target.value);
          if (!Number.isFinite(parsed)) return;
          if (parsed < 0) return;
          if (!allowZero && parsed === 0) return;
          onChange(parsed);
        }}
        min={allowZero ? 0 : 1}
        step={0.1}
        className="h-10 px-3 rounded-xl bg-white ring-1 ring-ink-200 text-sm text-ink-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-ink-900/30"
      />
    </label>
  );
}

function Spinner() {
  return (
    <span className="inline-block size-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
  );
}
